import z from 'zod';
/**
 * The project's Current TSN/CSN answer (DR-3769) — one elected engine row, not a
 * flat array.
 *
 * Unlike `nis-events-ready` (a union of every statement in the project) this family
 * answers ONE question: what were the working engine's clocks at the newest statement
 * that names it. A multi-engine "Time in Service Statement" lists both engines of an
 * airframe, so the row is elected by serial match, then by statement date.
 *
 * `status` carries the reason as well as the result, and the two are not
 * interchangeable. `clocks-missing` ships a `winner` on purpose: the consumer must be
 * able to say "the newest statement is there but its clocks did not extract" and link
 * to the page, rather than silently answering from a superseded document.
 *
 * `none` covers TWO states and is deliberately one shape for both: the project holds such
 * documents but no page of them produced a reading, OR the project LOST its last statement
 * document (DR-3861) — the retraction, which carries `statementPageCount: 0` and no
 * `winner`. Either way the answer is gone, so the consumer clears its winner columns on any
 * `none` and needs no second code path. A project that NEVER held a statement document emits
 * no envelope on this family at all.
 */
export const tsnCsnStatementReadyWebhookSchema = z.looseObject({
    type: z.literal('tsn-csn-statement-ready'),
    projectId: z.string(),
    // `z.iso.datetime()`, not a bare string: the consumer contract is an ORDERABLE instant,
    // so the schema enforces what the field documents — same spelling as `nis-events-ready`.
    //
    // `.optional()` is a ROLLOUT concession, not a statement that the stamp is dispensable.
    // Brain always emits it (pinned by the builder's test). This schema is a member of
    // `webhookPayloadSchema`, so a REQUIRED field would make a consumer that upgrades
    // `@avisoma/shared` reject every older, unstamped envelope still in flight (DR-3278).
    generatedAt: z.iso.datetime().optional(),
    generation: z.number().int().positive().max(2_147_483_647).optional(),
    status: z.enum(['ok', 'clocks-missing', 'no-match', 'none']),
    /** The working engine serial the rollup matched against; null when the project has none. */
    projectEsn: z.string().nullable(),
    /** Absent for `no-match` / `none`; present for BOTH `ok` and `clocks-missing`. */
    winner: z
        .looseObject({
        pageId: z.string(),
        documentId: z.string(),
        statementDate: z.string().nullable(),
        aircraftRegistration: z.string().nullable(),
        msn: z.string().nullable(),
        operator: z.string().nullable(),
        esn: z.string(),
        tsn: z.number().nullable(),
        csn: z.number().nullable(),
    })
        .optional(),
    /**
     * How many Current TSN and CSN Statement pages CARRIED a reading — not how many pages
     * the project has classified as statements. A page whose extraction yielded nothing
     * settles nothing, so counting it would report evidence the project does not have.
     * Zero for `none`: either no page produced a reading, or the last statement document
     * was removed (the DR-3861 retraction).
     */
    statementPageCount: z.number().int().nonnegative(),
});
//# sourceMappingURL=tsn-csn-statement-ready.webhook.js.map